import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  FormGroup,
  Label,
  Button,
  Input,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { hotelreservationmonthly } from "helpers/requests";

const STATUS_CONFIG = {
  PENDING:        { color: "warning",   label: "En attente",     icon: "bx-time-five" },
  ACCEPTED:       { color: "success",   label: "Acceptée",       icon: "bx-check-circle" },
  REFUSED:        { color: "danger",    label: "Refusée",        icon: "bx-x-circle" },
  CANCELLED:      { color: "secondary", label: "Annulée",        icon: "bx-block" },
  HOTEL_ACCEPTED: { color: "primary",   label: "Acceptée hôtel", icon: "bx-buildings" },
  HOTEL_REFUSED:  { color: "danger",    label: "Refusée hôtel",  icon: "bx-buildings" },
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
const formatMoney = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("fr-DZ") + " DA" : "—";

const HotelStatistique = (props) => {
  const [monthlyReservations, setMonthlyReservations] = useState({});
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [propertySearch, setPropertySearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const monthlyhotel = await hotelreservationmonthly();
        setMonthlyReservations(monthlyhotel);
      } catch (error) {
        console.error("Error fetching hotel data:", error);
      }
    };
    fetchReservations();
  }, []);

  const propertyOptions = useMemo(
    () =>
      Object.values(monthlyReservations || {})
        .flat()
        .reduce((acc, reservation) => {
          const existing = acc.find((p) => p.id === reservation.propertyId);
          if (!existing) {
            acc.push({
              id: reservation.propertyId,
              name: reservation.property?.name || "—",
              reservations: [reservation],
            });
          } else {
            existing.reservations.push(reservation);
          }
          return acc;
        }, []),
    [monthlyReservations]
  );

  const filteredProperties = useMemo(
    () =>
      propertyOptions.filter((p) =>
        (p.name || "").toLowerCase().includes(propertySearch.toLowerCase())
      ),
    [propertyOptions, propertySearch]
  );

  // Re-filter when filters change
  useEffect(() => {
    if (!selectedProperty) {
      setFilteredReservations([]);
      return;
    }
    let filtered = [...selectedProperty.reservations];

    if (startDate && endDate) {
      filtered = filtered.filter((r) => {
        const d = new Date(r.createdAt);
        return d >= startDate && d <= endDate;
      });
    }
    if (statusFilter !== "ALL") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.email || "").toLowerCase().includes(q)
      );
    }
    setFilteredReservations(filtered);
  }, [selectedProperty, startDate, endDate, statusFilter, searchTerm]);

  // Statistics derived from current filtered set
  const statistics = useMemo(() => {
    const statusCounts = Object.keys(STATUS_CONFIG).reduce((acc, s) => {
      acc[s] = 0;
      return acc;
    }, {});
    filteredReservations.forEach((r) => {
      if (r.status in statusCounts) statusCounts[r.status] += 1;
    });
    const totalAcceptedPrice = filteredReservations
      .filter((r) => r.status === "HOTEL_ACCEPTED" || r.status === "ACCEPTED")
      .reduce((sum, r) => sum + Number(r.totalPrice || 0), 0);
    const totalAll = filteredReservations.reduce(
      (sum, r) => sum + Number(r.totalPrice || 0),
      0
    );
    const avg =
      filteredReservations.length > 0 ? totalAll / filteredReservations.length : 0;
    return {
      totalReservations: filteredReservations.length,
      statusCounts,
      totalAcceptedPrice,
      averageReservationPrice: Number(avg.toFixed(2)),
    };
  }, [filteredReservations]);

  const handlePropertySelect = (property) => {
    setPropertySearch(property.name);
    setSelectedProperty(property);
    setFilteredReservations(property.reservations);
    setIsDropdownOpen(false);
    setStartDate(null);
    setEndDate(null);
    setStatusFilter("ALL");
    setSearchTerm("");
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const openReservationDetails = (reservation) => {
    setReservationDetails(reservation);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setReservationDetails(null);
  };

  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStatusFilter("ALL");
    setSearchTerm("");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) setTimeout(() => inputRef.current?.focus(), 0);
  };

  document.title = "Statistiques hôtel";

  // Mini-stat tiles config (Skote pattern)
  const kpis = [
    {
      title: "Total réservations",
      value: statistics.totalReservations,
      iconClass: "bx-list-ul",
      bg: "primary",
    },
    {
      title: "En attente",
      value: statistics.statusCounts.PENDING,
      iconClass: "bx-time-five",
      bg: "warning",
    },
    {
      title: "Acceptées",
      value:
        statistics.statusCounts.ACCEPTED +
        statistics.statusCounts.HOTEL_ACCEPTED,
      iconClass: "bx-check-circle",
      bg: "success",
    },
    {
      title: "Revenus confirmés",
      value: formatMoney(statistics.totalAcceptedPrice),
      iconClass: "bx-money",
      bg: "danger",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Statistiques")}
            breadcrumbItem={props.t("Réservations hôtel")}
          />

          {/* Property selector */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">
                    <i className="bx bx-buildings text-primary me-2" />
                    {props.t("Sélection de l'hôtel")}
                  </CardTitle>
                  <Row>
                    <Col md={8}>
                      <FormGroup className="mb-0">
                        <Label>Rechercher un hôtel partenaire</Label>
                        <div className="position-relative" ref={dropdownRef}>
                          <div className="d-flex">
                            <Input
                              innerRef={inputRef}
                              type="text"
                              placeholder="Tapez le nom d'un hôtel…"
                              value={propertySearch}
                              onChange={(e) => {
                                setPropertySearch(e.target.value);
                                if (!isDropdownOpen) setIsDropdownOpen(true);
                              }}
                              onFocus={() => setIsDropdownOpen(true)}
                            />
                            <Button color="light" onClick={toggleDropdown} className="border ms-1">
                              <i
                                className={`bx bx-chevron-${
                                  isDropdownOpen ? "up" : "down"
                                }`}
                              />
                            </Button>
                          </div>

                          {isDropdownOpen && (
                            <div
                              className="dropdown-menu w-100 show shadow"
                              style={{
                                position: "absolute",
                                zIndex: 1000,
                                maxHeight: 300,
                                overflowY: "auto",
                                display: "block",
                                marginTop: 4,
                              }}
                            >
                              {filteredProperties.length > 0 ? (
                                filteredProperties.map((property) => (
                                  <button
                                    key={property.id}
                                    type="button"
                                    onClick={() => handlePropertySelect(property)}
                                    className={`dropdown-item d-flex justify-content-between align-items-center ${
                                      selectedProperty?.id === property.id
                                        ? "active"
                                        : ""
                                    }`}
                                  >
                                    <span>
                                      <i className="bx bx-buildings me-2 text-muted" />
                                      {property.name}
                                    </span>
                                    <Badge color="light" className="text-muted">
                                      {property.reservations.length} rés.
                                    </Badge>
                                  </button>
                                ))
                              ) : (
                                <div className="dropdown-item text-muted">
                                  Aucun hôtel trouvé
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                    {selectedProperty && (
                      <Col md={4} className="d-flex align-items-end justify-content-md-end">
                        <Button
                          color="light"
                          onClick={() => {
                            setSelectedProperty(null);
                            setPropertySearch("");
                            resetFilters();
                          }}
                        >
                          <i className="bx bx-x me-1" /> Réinitialiser
                        </Button>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {!selectedProperty ? (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody className="text-center py-5">
                    <div className="avatar-md mx-auto mb-3">
                      <span className="avatar-title rounded-circle bg-light text-primary font-size-24">
                        <i className="bx bx-buildings" />
                      </span>
                    </div>
                    <h5 className="mt-3">Aucun hôtel sélectionné</h5>
                    <p className="text-muted mb-0">
                      Choisissez un hôtel partenaire ci-dessus pour afficher
                      ses statistiques et son historique de réservations.
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <>
              {/* KPI mini-stats — Skote pattern */}
              <Row>
                {kpis.map((kpi, i) => (
                  <Col md={3} key={i}>
                    <Card className="mini-stats-wid">
                      <CardBody>
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-2">
                              {kpi.title}
                            </p>
                            <h4 className="mb-0">{kpi.value}</h4>
                          </div>
                          <div
                            className={`avatar-sm rounded-circle bg-${kpi.bg} align-self-center mini-stat-icon`}
                          >
                            <span
                              className={`avatar-title rounded-circle bg-${kpi.bg}`}
                            >
                              <i className={`bx ${kpi.iconClass} font-size-24`} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row>
                <Col md={6}>
                  <Card>
                    <CardBody>
                      <p className="text-muted fw-medium mb-2">
                        Panier moyen
                      </p>
                      <h4 className="mb-0">
                        {formatMoney(statistics.averageReservationPrice)}
                      </h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <CardBody>
                      <p className="text-muted fw-medium mb-2">
                        Refusées / annulées
                      </p>
                      <h4 className="mb-0">
                        {statistics.statusCounts.REFUSED +
                          statistics.statusCounts.HOTEL_REFUSED +
                          statistics.statusCounts.CANCELLED}
                      </h4>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Filters */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3 d-flex justify-content-between align-items-center">
                        <span>
                          <i className="bx bx-filter-alt text-primary me-2" />
                          Filtres
                        </span>
                        <Button
                          color="link"
                          size="sm"
                          onClick={resetFilters}
                          className="p-0"
                        >
                          Réinitialiser
                        </Button>
                      </CardTitle>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Période</Label>
                            <DatePicker
                              selectsRange
                              startDate={startDate}
                              endDate={endDate}
                              onChange={handleDateRangeChange}
                              isClearable
                              placeholderText="Sélectionner une période"
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Statut</Label>
                            <Input
                              type="select"
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                            >
                              <option value="ALL">Tous les statuts</option>
                              {Object.keys(STATUS_CONFIG).map((status) => (
                                <option key={status} value={status}>
                                  {STATUS_CONFIG[status].label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Recherche</Label>
                            <Input
                              type="text"
                              placeholder="Nom ou e-mail du client…"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Reservation table */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-1">
                        Historique des réservations
                      </CardTitle>
                      <CardSubtitle className="text-muted mb-3">
                        {selectedProperty.name} · {filteredReservations.length}{" "}
                        résultat{filteredReservations.length !== 1 ? "s" : ""}
                      </CardSubtitle>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Client</th>
                              <th>E-mail</th>
                              <th>Arrivée</th>
                              <th>Départ</th>
                              <th>Statut</th>
                              <th>Montant</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredReservations.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="text-center text-muted py-4">
                                  Aucune réservation ne correspond aux filtres.
                                </td>
                              </tr>
                            ) : (
                              filteredReservations.map((r, i) => {
                                const cfg =
                                  STATUS_CONFIG[r.status] || {
                                    color: "secondary",
                                    label: r.status,
                                    icon: "bx-help-circle",
                                  };
                                return (
                                  <tr key={r.id || i}>
                                    <td>
                                      <strong>{r.name || "—"}</strong>
                                      {r.surnom && (
                                        <div className="text-muted font-size-12">
                                          {r.surnom}
                                        </div>
                                      )}
                                    </td>
                                    <td>{r.email || "—"}</td>
                                    <td>{formatDate(r.checkinDate)}</td>
                                    <td>{formatDate(r.checkoutDate)}</td>
                                    <td>
                                      <Badge
                                        color={cfg.color}
                                        className="font-size-11"
                                      >
                                        <i className={`bx ${cfg.icon} me-1`} />
                                        {cfg.label}
                                      </Badge>
                                    </td>
                                    <td className="fw-bold">
                                      {formatMoney(r.totalPrice)}
                                    </td>
                                    <td className="text-end">
                                      <Button
                                        color="info"
                                        size="sm"
                                        outline
                                        onClick={() =>
                                          openReservationDetails(r)
                                        }
                                      >
                                        <i className="mdi mdi-eye me-1" />
                                        Détails
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* Detail modal — Skote pattern */}
          <Modal isOpen={isModalOpen} toggle={closeModal} size="lg" centered>
            <ModalHeader toggle={closeModal}>
              Détails de la réservation
            </ModalHeader>
            <ModalBody>
              {reservationDetails && (
                <Row>
                  <Col md={12} className="mb-3">
                    <h5 className="mb-1">
                      {reservationDetails.name} {reservationDetails.surnom}
                    </h5>
                    <Badge
                      color={
                        STATUS_CONFIG[reservationDetails.status]?.color ||
                        "secondary"
                      }
                      className="font-size-12"
                    >
                      {STATUS_CONFIG[reservationDetails.status]?.label ||
                        reservationDetails.status}
                    </Badge>
                  </Col>

                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">E-mail</p>
                    <p className="mb-0">{reservationDetails.email || "—"}</p>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Téléphone</p>
                    <p className="mb-0">{reservationDetails.phone || "—"}</p>
                  </Col>

                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Hôtel</p>
                    <p className="mb-0">
                      {reservationDetails.property?.name || "—"}
                    </p>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Adresse</p>
                    <p className="mb-0">{reservationDetails.address || "—"}</p>
                  </Col>

                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Arrivée</p>
                    <p className="mb-0">
                      {formatDate(reservationDetails.checkinDate)}
                    </p>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Départ</p>
                    <p className="mb-0">
                      {formatDate(reservationDetails.checkoutDate)}
                    </p>
                  </Col>

                  <Col md={4} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Nuits</p>
                    <p className="mb-0">{reservationDetails.totalNights}</p>
                  </Col>
                  <Col md={4} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Adultes</p>
                    <p className="mb-0">{reservationDetails.numAdults}</p>
                  </Col>
                  <Col md={4} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Enfants</p>
                    <p className="mb-0">{reservationDetails.numChildren}</p>
                  </Col>

                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Paiement</p>
                    <Badge
                      color={reservationDetails.ispayed ? "success" : "warning"}
                      className="font-size-11"
                    >
                      {reservationDetails.ispayed ? "Payé" : "Non payé"}
                    </Badge>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Montant total</p>
                    <h5 className="text-primary mb-0">
                      {formatMoney(reservationDetails.totalPrice)}
                    </h5>
                  </Col>

                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Créée le</p>
                    <p className="mb-0">
                      {formatDateTime(reservationDetails.createdAt)}
                    </p>
                  </Col>
                  <Col md={6} className="mb-3">
                    <p className="text-muted fw-medium mb-1">Modifiée le</p>
                    <p className="mb-0">
                      {formatDateTime(reservationDetails.updatedAt)}
                    </p>
                  </Col>
                </Row>
              )}
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

HotelStatistique.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(HotelStatistique);
